import { useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { FileTextIcon, Trash2Icon, UploadIcon } from 'lucide-react'
import {
	Alert, AlertTitle,
	Button, ConfirmDialog, IconButton, Select, SelectContent, SelectItem,
	SelectTrigger, SelectValue, Spinner,
} from '@rentflowdev/ui'
import {
	useDeleteCompanyDocumentMutation,
	useGenerateDocumentUploadUrlMutation,
	useListCompanyDocumentsQuery,
	useUploadCompanyDocumentMutation,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import type { CompanyDocumentKind } from '@/types'

export const Route = createFileRoute('/_app/settings/documents')({
	component: SettingsDocumentsPage,
})

const KINDS: CompanyDocumentKind[] = [
	'REGISTRATION_CERTIFICATE',
	'EGRUL_EXCERPT',
	'CHARTER',
	'OTHER',
]

const DEFAULT_LOCALE = 'ru-RU'

function formatBytes(bytes?: number): string {
	if (!bytes) return ''
	if (bytes < 1024) return `${bytes} Б`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
	return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: 'numeric',
	})
}

function SettingsDocumentsPage() {
	const { t } = useTranslation()
	const fileInputRef = useRef<HTMLInputElement>(null)

	const { data, isLoading } = useListCompanyDocumentsQuery()
	const [generateUploadUrl]   = useGenerateDocumentUploadUrlMutation()
	const [uploadDocument]      = useUploadCompanyDocumentMutation()
	const [deleteDocument]      = useDeleteCompanyDocumentMutation()

	const [selectedKind, setSelectedKind] = useState<CompanyDocumentKind>('OTHER')
	const [uploading, setUploading]       = useState(false)
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
	const [error, setError]               = useState<string | null>(null)

	const documents = data?.items ?? []

	async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file) return
		setError(null)
		setUploading(true)
		try {
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()

			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open('PUT', uploadUrl)
				xhr.setRequestHeader('Content-Type', file.type)
				xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
				xhr.onerror = () => reject(new Error('Network error'))
				xhr.send(file)
			})

			await uploadDocument({
				kind:     selectedKind,
				fileUrl:  publicUrl,
				fileName: file.name,
				fileSize: file.size,
				mimeType: file.type,
			}).unwrap()
		} catch (err) {
			setError(extractErrorMessage(err) ?? t('settings.companyDocuments.errors.upload'))
		} finally {
			setUploading(false)
		}
	}

	async function handleDelete(id: string) {
		setError(null)
		try {
			await deleteDocument(id).unwrap()
		} catch (err) {
			setError(extractErrorMessage(err) ?? t('settings.companyDocuments.errors.delete'))
		} finally {
			setDeleteTarget(null)
		}
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<div className="flex items-center justify-between border-b border-(--color-border) px-6 py-5">
				<h1 className="text-xl font-[500]">{t('settings.companyDocuments.title')}</h1>
				<div className="flex items-center gap-2">
					<Select value={selectedKind} onValueChange={(v) => setSelectedKind(v as CompanyDocumentKind)}>
						<SelectTrigger className="w-56">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{KINDS.map((k) => (
								<SelectItem key={k} value={k}>{t(`settings.companyDocuments.kinds.${k}`)}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button
						iconLeft={uploading ? <Spinner size="sm" /> : <UploadIcon className="size-4" />}
						disabled={uploading}
						onClick={() => fileInputRef.current?.click()}
					>
						{t('settings.companyDocuments.upload')}
					</Button>
				</div>
			</div>

			<div className="flex-1 overflow-auto px-6 py-5">
				{error && (
					<Alert variant="destructive" className="mb-4">
						<AlertTitle>{error}</AlertTitle>
					</Alert>
				)}

				{isLoading ? (
					<Spinner size="sm" />
				) : documents.length === 0 ? (
					<p className="text-sm text-(--color-muted-foreground)">{t('settings.companyDocuments.empty')}</p>
				) : (
					<div className="flex max-w-2xl flex-col gap-2">
						{documents.map((doc) => (
							<div
								key={doc.id}
								className="flex items-center gap-3 rounded-(--radius-md) border border-(--color-border) bg-(--color-card) px-4 py-3"
							>
								<FileTextIcon className="size-5 shrink-0 text-(--color-muted-foreground)" />
								<div className="flex flex-1 min-w-0 flex-col gap-0.5">
									<a
										href={doc.fileUrl}
										target="_blank"
										rel="noreferrer"
										className="truncate text-sm font-[500] text-(--color-foreground) hover:underline"
									>
										{doc.fileName}
									</a>
									<span className="text-xs text-(--color-muted-foreground)">
										{t(`settings.companyDocuments.kinds.${doc.kind}`)}
										{doc.fileSize ? ` · ${formatBytes(doc.fileSize)}` : ''}
										{` · ${formatDate(doc.uploadedAt)}`}
									</span>
								</div>
								<IconButton
									variant="ghost"
									size="sm"
									type="button"
									aria-label={t('common.delete')}
									onClick={() => setDeleteTarget(doc.id)}
								>
									<Trash2Icon className="size-4" />
								</IconButton>
							</div>
						))}
					</div>
				)}
			</div>

			<input
				ref={fileInputRef}
				type="file"
				className="hidden"
				accept="application/pdf,.doc,.docx,image/jpeg,image/png,image/webp,image/heic,image/heif"
				onChange={handleFilePicked}
			/>

			<ConfirmDialog
				open={deleteTarget !== null}
				onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
				title={t('settings.companyDocuments.deleteConfirm.title')}
				description={t('settings.companyDocuments.deleteConfirm.description')}
				cancelLabel={t('settings.companyDocuments.deleteConfirm.cancel')}
				confirmLabel={t('settings.companyDocuments.deleteConfirm.confirm')}
				variant="destructive"
				onConfirm={() => { if (deleteTarget) void handleDelete(deleteTarget) }}
			/>
		</div>
	)
}
